import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) return;
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => { formBody.append(key, value); });
      const response = await fetch('https://readdy.ai/api/form/d61giuv4klnl5oj67o10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', lastName: '', email: '', phone: '', company: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: 'ri-mail-line', title: 'Email', details: 'info@jbsconsulting.xyz', subtext: 'We reply within 24 hours' },
    { icon: 'ri-map-pin-line', title: 'Office', details: '8 The Green, Suite A', subtext: 'Dover, DE 19901' },
    { icon: 'ri-building-line', title: 'Company', details: 'JBS Consulting, LLC', subtext: 'Business Advisory & Turnaround' },
    { icon: 'ri-lock-line', title: 'Confidential', details: 'All inquiries are private', subtext: 'Your information stays with us' }
  ];

  const faqs = [
    {
      question: 'How quickly can JBS Consulting engage with my business?',
      answer: 'We can typically begin an initial assessment within 48 hours of your first contact. In urgent situations, we can mobilize same-day.'
    },
    {
      question: 'How are your fees structured?',
      answer: 'We work on a fixed fee and fixed timeline basis. You will know exactly what you will pay before we begin — no hourly billing surprises.'
    },
    {
      question: 'Is my inquiry confidential?',
      answer: 'Absolutely. All inquiries and engagements are handled with complete confidentiality. We understand the sensitivity of financial distress situations.'
    },
    {
      question: 'What size businesses do you work with?',
      answer: 'We specialize in small and lower middle market companies — typically businesses with $1M to $100M in annual revenue across a wide range of industries.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6">Contact Us</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Is your business in distress? Reach out today for a confidential, no-obligation assessment. We're here to help you find a way forward.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {contactInfo.map((info, index) => (
              <div key={index} className="bg-slate-50 rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${info.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-serif font-bold text-slate-900 mb-2">{info.title}</h3>
                <p className="text-base text-slate-700 font-medium mb-1">{info.details}</p>
                <p className="text-sm text-slate-500">{info.subtext}</p>
              </div>
            ))}
          </div>

          {/* Contact Form and Map */}
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-serif font-bold text-slate-900 mb-6">Send Us a Message</h2>
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">First Name *</label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Last Name *</label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  <div className="mt-2 flex items-start gap-2">
                    <input type="checkbox" name="smsConsent" id="contact-sms-consent" className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-orange-500 cursor-pointer flex-shrink-0" />
                    <label htmlFor="contact-sms-consent" className="text-xs text-slate-500 leading-relaxed cursor-pointer">
                      By providing your phone number, you consent to receive calls and SMS text messages from JBS Consulting, LLC regarding your inquiry. Message &amp; data rates may apply. Reply STOP to opt out at any time. View our <a href="/privacy-policy" className="underline hover:text-orange-500 transition-colors">Privacy Policy</a>.
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
                  <input type="text" name="company" value={formData.company} onChange={handleChange} className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">How Can We Help?</label>
                  <select name="subject" value={formData.subject} onChange={handleChange} className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
                    <option value="">Select a topic</option>
                    <option value="Turnaround Services">Turnaround Services</option>
                    <option value="Cash Flow Coach">Cash Flow Coach</option>
                    <option value="Cost Reduction">Cost Reduction</option>
                    <option value="Process Automation">Process Automation</option>
                    <option value="Capital Solutions">Capital Solutions</option>
                    <option value="CXO Services">CXO / Interim Management</option>
                    <option value="General Inquiry">General Inquiry</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message * (Max 500 characters)</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required maxLength={500} rows={6} className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"></textarea>
                  <p className="text-xs text-slate-500 mt-2">{formData.message.length}/500 characters</p>
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full px-8 py-4 bg-orange-500 text-white text-base font-medium rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">Thank you! Your message has been sent. We'll respond within 24 hours.</p>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">Sorry, there was an error. Please try again or call us directly.</p>
                  </div>
                )}
              </form>
            </div>

            <div>
              <h2 className="text-3xl font-serif font-bold text-slate-900 mb-6">Visit Our Office</h2>
              <div className="bg-slate-100 rounded-lg overflow-hidden h-[600px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3083.0!2d-75.5244!3d39.1582!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c7aa3b6b6b6b6b%3A0x0!2s8+The+Green%2C+Suite+A%2C+Dover%2C+DE+19901!5e0!3m2!1sen!2sus!4v1710000000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="JBS Consulting Office Location - Dover, DE"
                ></iframe>
              </div>
              <div className="mt-6 p-6 bg-slate-50 rounded-lg">
                <h3 className="text-lg font-serif font-bold text-slate-900 mb-3">JBS Consulting, LLC</h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                      <i className="ri-map-pin-line text-orange-500 text-base"></i>
                    </div>
                    <p className="text-sm text-slate-600">8 The Green, Suite A<br />Dover, DE 19901</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line text-orange-500 text-base"></i>
                    </div>
                    <a href="mailto:info@jbsconsulting.xyz" className="text-sm text-slate-600 hover:text-orange-500 transition-colors">info@jbsconsulting.xyz</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-slate-600">Quick answers about working with JBS Consulting</p>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-serif font-bold text-slate-900 mb-3">{faq.question}</h3>
                <p className="text-sm text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
